import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { PipelineStore } from '@vanilla/constants/Pipelines.js';
import { PipelinesApiService } from '@vanilla/services/crm/PipelinesApiService.js';
import { toastManager } from '@/lib/toast.js';
import { acquireQcPopupOpen, releaseQcPopupOpen } from '@/lib/qcPopupOpen.js';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard.js';

export function PipelineQuickCreateDialog({ open, onOpenChange, editEntity, onSaved }) {
  const isEdit = !!editEntity;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState('');
  const closingRef = useRef(false);

  useLayoutEffect(() => {
    if (!open) return undefined;
    acquireQcPopupOpen();
    return () => releaseQcPopupOpen();
  }, [open]);

  useEffect(() => {
    const nextName = editEntity ? (editEntity.name || '') : '';
    const nextDescription = editEntity ? (editEntity.description || '') : '';
    setName(nextName);
    setDescription(nextDescription);
    setError('');
    setBaseline(JSON.stringify({ name: nextName, description: nextDescription }));
  }, [editEntity, open]);

  const isDirty = useMemo(() => {
    if (!open || !baseline) return false;
    return JSON.stringify({ name, description }) !== baseline;
  }, [open, name, description, baseline]);
  const { requestClose } = useUnsavedGuard(isDirty);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const closeSafely = async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    try {
      await requestClose(() => onOpenChange(false));
    } finally {
      closingRef.current = false;
    }
  };

  const handleOpenChange = (next) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    void closeSafely();
  };

  const guardDismiss = (e) => {
    const t = e.target;
    if (t instanceof Element && t.closest('.quick-create-btn, .quick-create-btn-group')) {
      e.preventDefault();
      return;
    }
    if (document.querySelector('[role="alertdialog"]')) {
      e.preventDefault();
      return;
    }
    if (isDirtyRef.current) {
      e.preventDefault();
      void closeSafely();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Pipeline name is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        saved = await PipelinesApiService.update(editEntity.id, { name: name.trim(), description: description.trim(), is_active: editEntity.is_active });
        saved = saved?.data || saved || { ...editEntity, name: name.trim(), description: description.trim() };
        PipelineStore.update({ ...editEntity, ...saved, name: name.trim(), description: description.trim() });
      } else {
        saved = await PipelinesApiService.create({ name: name.trim(), description: description.trim() });
        saved = saved?.data || saved;
        if (saved?.id) PipelineStore.add(saved);
      }
      const savedId = saved?.id || editEntity?.id;
      if (savedId && onSaved) await onSaved(saved || { id: savedId, name: name.trim() });
      toastManager.success(isEdit ? 'Pipeline updated' : 'Pipeline created', `"${name.trim()}" has been ${isEdit ? 'updated' : 'created'} successfully.`);
      onOpenChange(false);
    } catch (err) {
      setError(`Failed to ${isEdit ? 'update' : 'create'} pipeline: ${err?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[380px] z-[1501]"
        overlayClassName="z-[1500] bg-black/50"
        aria-describedby={undefined}
        onPointerDownOutside={guardDismiss}
        onInteractOutside={guardDismiss}
        onEscapeKeyDown={(e) => {
          if (document.querySelector('[role="alertdialog"]')) {
            e.preventDefault();
            return;
          }
          if (isDirtyRef.current) {
            e.preventDefault();
            void closeSafely();
          }
        }}
        onFocusOutside={(e) => {
          if (document.querySelector('[role="alertdialog"]')) e.preventDefault();
        }}
      >
        <DialogTitle>{isEdit ? 'Edit Pipeline' : 'Quick Create Pipeline'}</DialogTitle>
        <DialogDescription className="sr-only">Pipeline form</DialogDescription>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div className="quick-create-field">
            <label htmlFor="qc-pipeline-name">Pipeline name <span className="required-asterisk">*</span></label>
            <Input id="qc-pipeline-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter pipeline name" required />
          </div>
          <div className="quick-create-field">
            <label htmlFor="qc-pipeline-desc">Description</label>
            <Textarea id="qc-pipeline-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Enter description" rows={3} />
          </div>
          {error && <div className="text-sm text-red-600" role="alert">{error}</div>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => void closeSafely()}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save' : 'Create')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
